import { daysBetween, shiftDays, todayISO, ymd } from './dates';

// Helpers for normalizing invoice/estimate line items coming from mixed sources
// (real CSV-imported invoices store `items` as a python-ish string; app-created
// invoices store an array of {desc, qty, rate}).

export const normalizeItems = (raw, fallbackTotal = 0) => {
  if (Array.isArray(raw) && raw.length) {
    return raw.map((it) => ({
      desc: it.desc || it.name || it.description || 'Service',
      qty: Number(it.qty ?? it.quantity ?? 1) || 1,
      rate:
        Number(
          it.rate ??
            (it.cost && (it.cost.amount ?? it.cost)) ??
            it.price ??
            0
        ) || 0,
    }));
  }
  if (typeof raw === 'string' && raw.trim()) {
    const nameMatch = raw.match(/'name':\s*'([^']*)'/);
    const amtMatch = raw.match(/'amount':\s*([\d.]+)/);
    return [
      {
        desc: nameMatch ? nameMatch[1] : 'Service',
        qty: 1,
        rate: amtMatch ? Number(amtMatch[1]) : Number(fallbackTotal) || 0,
      },
    ];
  }
  return [{ desc: 'Service', qty: 1, rate: Number(fallbackTotal) || 0 }];
};

export const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(n) || 0
  );

// MM/DD/YYYY (matches the real imported data format)
export const fmtDateUS = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt)) return '';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${dt.getFullYear()}`;
};

// parse MM/DD/YYYY or ISO into a Date
export const parseDate = (s) => {
  if (!s) return new Date();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [m, d, y] = s.split('/');
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(s);
};

// next numeric id within an array, looking at `number` then digits in `id`
export const nextNumber = (arr, start = 1000) => {
  const nums = (arr || [])
    .map((x) => parseInt(x.number || String(x.id || '').replace(/\D/g, ''), 10))
    .filter((n) => !isNaN(n));
  return (nums.length ? Math.max(...nums) : start) + 1;
};

// ---------------------------------------------------------------------------
// Records reach this app under two naming conventions: rows created in the app
// carry `paid`/`amountDue`/`customerAccountNumber`, while the imported
// production data carries `amountReceived`/`balance`/`customerAccount`. Reading
// only the first set reported $150 collected against a book holding $112,610,
// because `paid` was present on 1 of 1,119 invoices. These helpers read both.
// ---------------------------------------------------------------------------

// First key actually carrying a value — keeps a real 0 from being skipped.
const firstPresent = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

const n = (v) => Number(v) || 0;

export const invoicePaid = (inv) =>
  n(firstPresent(inv, 'paid', 'amountReceived', 'totalReceived'));

export const invoiceTotal = (inv) => n(firstPresent(inv, 'total', 'amount'));

export const invoiceOutstanding = (inv) => {
  const total = invoiceTotal(inv);
  if (total) return Math.round((total - invoicePaid(inv)) * 100) / 100;
  return n(firstPresent(inv, 'amountDue', 'balance'));
};

// Net 30 by default - the customer has this many days from the date of service.
// Overridden by the company's own setting.
export const DEFAULT_TERMS_DAYS = 30;

/**
 * When an invoice falls due, as YYYY-MM-DD.
 *
 * Not one invoice in production carries a `due` field - the importer never
 * wrote one - so every caller that read it got nothing and treated the invoice
 * as not overdue. The collections page aged all 316 unpaid invoices at zero
 * days while $31,090 of that was more than 90 days old and the oldest was 443.
 *
 * An explicit due date wins; otherwise it is derived from the terms. Nothing
 * is written back, so the rule stays right if the terms change.
 */
export const invoiceDue = (inv, termsDays = DEFAULT_TERMS_DAYS) => {
  const explicit = ymd(inv?.due || '');
  if (explicit) return explicit;
  const issued = ymd(inv?.date || inv?.serviceDate || inv?.issueDate || inv?.createdAt || '');
  if (!issued) return '';
  return shiftDays(issued, Math.max(0, Number(termsDays) || 0));
};

/**
 * Whole days past due, or null when the invoice carries no readable date at
 * all. Zero or negative means it is not yet due - null and 0 must stay
 * distinct, because "cannot tell" and "due today" are different answers.
 */
export const invoiceDaysOverdue = (inv, termsDays = DEFAULT_TERMS_DAYS, today = todayISO()) => {
  const due = invoiceDue(inv, termsDays);
  if (!due) return null;
  return daysBetween(due, today);
};

// Customer account number, under either name.
export const acctNumber = (row) =>
  String(firstPresent(row, 'customerAccountNumber', 'customerAccount') ?? '').trim();

const custName = (row) =>
  String(firstPresent(row, 'customer', 'customerName') ?? '').trim().toLowerCase();

// match invoices/workorders/payments to a customer by id, account number or name
export const belongsToCustomer = (row, customer) => {
  if (!customer || !row) return false;
  if (row.customerId && row.customerId === customer.id) return true;
  const acct = acctNumber(row);
  if (acct && customer.accountNumber && acct === String(customer.accountNumber).trim()) return true;
  // Imported work orders and payments carry only the customer's name — without
  // this, 1,137 of 1,146 jobs and every payment sat unlinked to any customer.
  const name = custName(row);
  if (name && customer.name && name === String(customer.name).trim().toLowerCase()) return true;
  return false;
};
